import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { getActiveLearner, setActiveLearner } from "@/lib/learner";
import { useLiveSync } from "@/lib/useLiveSync";
import LearnerPicker from "@/components/LearnerPicker";
import ReadingExperience from "@/components/ReadingExperience";
import DemoBanner from "@/components/DemoBanner";
import { toast } from "sonner";
import { Eye, MessageCircleHeart, ArrowRight, X } from "lucide-react";

export default function LearnerModule() {
  const { moduleId } = useParams();
  const [search] = useSearchParams();
  const isPreview = search.get("preview") === "1";
  const [mod, setMod] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const user = useMemo(getUser, []);
  const [learner, setLearner] = useState(() => {
    const existing = getActiveLearner();
    if (existing) return existing;
    if (user?.role === "learner" && user?.learner_id) {
      const auto = { id: user.learner_id, name: user.name, email: user.email, department: "" };
      setActiveLearner(auto);
      return auto;
    }
    if (isPreview) return { id: null, name: "Admin Preview" };
    return null;
  });
  // Picker only needed for admin preview without a chosen learner.
  const [pickerOpen, setPickerOpen] = useState(!learner && user?.role !== "learner");

  const reload = React.useCallback(() => {
    setLoadError(null);
    api.get(`/modules/${moduleId}`)
      .then(({ data }) => setMod(data))
      .catch((err) => {
        const msg = err?.response?.status === 404
          ? "This module no longer exists."
          : err?.response?.status === 403
            ? "This module hasn't been published yet."
            : err?.message || "Failed to load the module.";
        setLoadError(msg);
      });
  }, [moduleId]);

  useEffect(() => { reload(); }, [reload]);

  // Live admin-edit sync: when admin saves this module, refetch instantly.
  useLiveSync({ kind: "module", id: moduleId, onUpdate: reload });

  // Floating "Share feedback" CTA — appears once the learner has read every
  // module (the feedback API checks this server-side via reading_progress).
  // Re-checked on mount + every 25s so it lights up the moment they cross
  // the bar on this very module.
  const [fbState, setFbState] = useState({ show: false, submitted: false });
  const [fbDismissed, setFbDismissed] = useState(() => sessionStorage.getItem("uvc_fb_cta_hidden") === "1");
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/feedback/eligibility");
        if (cancelled) return;
        if (data.already_submitted) {
          setFbState({ show: false, submitted: true });
        } else if (data.eligible) {
          // First time we see eligibility flip true — celebrate.
          setFbState((prev) => {
            if (!prev.show && !prev.submitted) {
              toast.success("🎉 You've read every module! Share what worked and what didn't.", {
                duration: 7000,
                action: { label: "Open feedback", onClick: () => { window.location.href = "/learn/feedback"; } },
              });
            }
            return { show: true, submitted: false };
          });
        }
      } catch (_) { /* ignore */ }
    };
    check();
    const id = setInterval(check, 25000);
    return () => { cancelled = true; clearInterval(id); };
  }, [moduleId]);

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center" data-testid="module-load-error">
          <div className="text-5xl mb-3">😶</div>
          <div className="text-lg font-display font-semibold text-slate-900 mb-1">Couldn&apos;t load this module</div>
          <p className="text-sm text-slate-500 mb-5">{loadError}</p>
          <div className="flex items-center justify-center gap-2">
            <button onClick={reload} data-testid="retry-load-button" className="bg-[#E53935] hover:bg-[#c5302d] text-white text-sm font-semibold px-4 py-2 rounded-full">
              Try again
            </button>
            <Link to="/learn" className="text-sm text-slate-500 hover:text-slate-900 px-4 py-2">
              Back to courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="module-loading-state">
        <div className="text-center">
          <div className="inline-block w-9 h-9 border-3 border-[#E53935] border-t-transparent rounded-full animate-spin mb-3"
               style={{ borderWidth: "3px" }}/>
          <div className="text-sm text-slate-500">Loading module…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DemoBanner />
      {isPreview && (
        <div className="bg-amber-100 border-b border-amber-200 px-6 py-1.5 text-[11px] text-amber-900 flex items-center justify-center gap-2 fixed top-0 left-0 right-0 z-50" data-testid="preview-banner">
          <Eye size={12}/> Admin preview — this is exactly what learners see.
        </div>
      )}
      <div className={isPreview ? "pt-7" : ""}>
        <ReadingExperience
          mod={mod}
          learner={learner}
          onClose={() => { window.location.href = `/learn`; }}
        />
      </div>

      <LearnerPicker
        open={pickerOpen}
        onOpenChange={(v) => { if (!v && !learner) return; setPickerOpen(v); }}
        onPicked={setLearner}
      />

      {/* Floating feedback CTA — only when learner has finished every module */}
      {fbState.show && !fbDismissed && (
        <div
          data-testid="floating-feedback-cta"
          className="fixed bottom-6 right-6 z-40 max-w-sm"
        >
          <div className="bg-white border border-[#E53935]/30 shadow-2xl rounded-xl overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#E53935] to-[#222222]"/>
            <button
              onClick={() => { sessionStorage.setItem("uvc_fb_cta_hidden", "1"); setFbDismissed(true); }}
              data-testid="dismiss-floating-feedback-cta"
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 p-1"
              aria-label="Dismiss"
            >
              <X size={14}/>
            </button>
            <div className="p-5 pt-6 flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-[#E53935]/10 border border-[#E53935]/30 text-[#E53935] flex items-center justify-center">
                <MessageCircleHeart size={18}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#E53935] font-bold mb-1">
                  You&apos;ve read everything
                </div>
                <div className="font-display text-base text-[#222222] leading-snug mb-1">
                  Help us improve the next version.
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  This is our first L&amp;D product — your honest take on each module shapes v2.
                </p>
                <Link
                  to="/learn/feedback"
                  data-testid="open-feedback-from-cta"
                  className="inline-flex items-center gap-1.5 bg-[#222222] hover:bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                >
                  Share feedback <ArrowRight size={12}/>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Also export Link helper for LearnerCourse import compatibility
export { Link };
